"use client";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import warning from "@/assets/warning.svg";
import { MoreVertical, Pencil, Trash2, Eye, XCircle } from "lucide-react";
import React, { useState } from "react";
import { useToast } from "@/components/ui/use-toast";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { FacilityBooking } from "@/types/facility/booking";
import { useDeleteFacilityBookingMutation } from "@/services/facilityApis";
import { ErrorType } from "@/types";
import type { CalendarEvent } from "./calendar";
import { isTrainingBooking } from "@/lib/helper";

/**
 * DeleteScope values MUST match what the backend accepts as the `scope` query param:
 *   "this"               → delete only this occurrence
 *   "this_and_following" → delete this occurrence and all future ones
 *   "all"                → delete every occurrence of this schedule
 */
export type DeleteScope = "this" | "this_and_following" | "all";

interface CalendarEventActionsProps {
  event: CalendarEvent;
  onEdit?: (event: CalendarEvent) => void;
  onView?: (event: CalendarEvent) => void;
  onDelete?: (event: CalendarEvent, scope: DeleteScope) => void;
  refetch?: () => void;
  access?: string;
  size?: "sm" | "md" | "lg";
  variant?: "dropdown" | "buttons";
}

export function CalendarEventActions({
  event,
  onEdit,
  onView,
  onDelete,
  refetch,
  access,
  size = "sm",
  variant = "dropdown",
}: CalendarEventActionsProps) {
  const { toast } = useToast();
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [deleteScope, setDeleteScope] = useState<DeleteScope>("this");

  const [deleteBooking, { isLoading: deleteLoading }] =
    useDeleteFacilityBookingMutation();

  const bookingData = event.originalData as FacilityBooking;

  // Training session check (created from Training Module)
  const isTraining = isTrainingBooking(event.originalData);

  // Schedule slot = has facility_schedule_id AND slot_date, and NOT a direct booking / training
  const isSchedule =
    !isTraining &&
    !String(event.id).startsWith("booking_") &&
    Boolean(event.originalData?.facility_schedule_id && event.originalData?.slot_date);

  const canEdit = isTraining
    ? false
    : isSchedule
    ? access === "full_access" || access === "write"
    : bookingData?.status === "Pending" && bookingData?.payment_status !== "Paid";

  const canDelete = isTraining
    ? access === "full_access" || access === "write"
    : isSchedule
    ? access === "full_access"
    : access === "full_access" && bookingData?.status === "Pending";

  // ── Booking delete (no scope needed) ───────────────────────────────
  const handleBookingDelete = async () => {
    if (!bookingData?.id || !refetch) return;
    try {
      await deleteBooking(bookingData.id as number).unwrap();
      refetch();
      toast({ variant: "success", title: "Facility Booking Deleted Successfully" });
      setIsDeleteOpen(false);
    } catch (error) {
      const typedError = error as ErrorType;
      toast({
        variant: "destructive",
        title: "Error in Deletion",
        description: `${typedError.data?.detail ||
          (typedError.data as { message?: string })?.message ||
          "Something went wrong"
          }`,
      });
    }
  };

  const handleEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    onEdit?.(event);
  };

  const handleView = (e: React.MouseEvent) => {
    e.stopPropagation();
    onView?.(event);
  };

  const handleDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setDeleteScope("this"); // always reset to "this" when modal opens
    setIsDeleteOpen(true);
  };

  // ── Confirm delete / cancel ─────────────────────────────────────────
  const handleConfirmDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isTraining && onDelete) {
      onDelete(event, "this");
      setIsDeleteOpen(false);
    } else if (isSchedule && onDelete) {
      onDelete(event, deleteScope);
      setIsDeleteOpen(false);
    } else {
      handleBookingDelete();
    }
  };

  // ── Scope radio option component ────────────────────────────────────
  const ScopeOption = ({
    value,
    label,
    description,
    activeColor,
  }: {
    value: DeleteScope;
    label: string;
    description: string;
    activeColor: string;
  }) => (
    <div
      className={`flex items-start space-x-3 p-3 rounded-lg border mb-2 last:mb-0 cursor-pointer transition-colors ${deleteScope === value ? activeColor : "border-gray-200 hover:border-gray-300 bg-white"
        }`}
      onClick={() => setDeleteScope(value)}
    >
      <RadioGroupItem value={value} id={`del-${value}-${event.id}`} className="mt-0.5 shrink-0" />
      <div>
        <Label htmlFor={`del-${value}-${event.id}`} className="cursor-pointer font-medium text-sm">
          {label}
        </Label>
        <p className="text-xs text-gray-500 mt-0.5">{description}</p>
      </div>
    </div>
  );

  // ── Delete / Cancel modal ────────────────────────────────────────────
  const renderDeleteDialog = () =>
    isDeleteOpen ? (
      <AlertDialog open={isDeleteOpen} onOpenChange={setIsDeleteOpen}>
        <AlertDialogContent onClick={(e) => e.stopPropagation()}>
          <AlertDialogHeader>
            <AlertDialogDescription asChild>
              <div>
                <div className="flex flex-col items-center justify-center gap-3 mb-4">
                  <img src={warning} alt="warning" className="w-14 h-14" />
                  <AlertDialogTitle className="text-lg font-semibold text-center">
                    {isTraining
                      ? "Cancel Training Class?"
                      : isSchedule
                      ? "Delete recurring schedule?"
                      : "Delete this facility booking?"}
                  </AlertDialogTitle>
                  <p className="text-sm text-gray-600 text-center">
                    {isTraining
                      ? `Are you sure you want to cancel this training class${
                          event.user?.name && event.user.name !== "Booked"
                            ? ` for ${event.user.name}`
                            : ""
                        }? The session credit will be refunded to the training package and the session will be freed up to reschedule.`
                      : isSchedule
                      ? "This action cannot be undone. Please choose which occurrences to delete:"
                      : "Are you sure you want to delete this facility booking? This action cannot be undone."}
                  </p>
                </div>

                {/* ── Scope selection — only for schedule slots ── */}
                {isSchedule && (
                  <RadioGroup
                    value={deleteScope}
                    onValueChange={(v) => setDeleteScope(v as DeleteScope)}
                    className="mt-2"
                  >
                    <ScopeOption
                      value="this"
                      label="This event only"
                      description="Only this single slot will be deleted."
                      activeColor="border-blue-400 bg-blue-50"
                    />
                    <ScopeOption
                      value="this_and_following"
                      label="This and following events"
                      description="This slot and all future slots of this schedule."
                      activeColor="border-amber-400 bg-amber-50"
                    />
                    <ScopeOption
                      value="all"
                      label="All events"
                      description="Every slot of this recurring schedule (past and future)."
                      activeColor="border-red-400 bg-red-50"
                    />
                  </RadioGroup>
                )}

                <div className="flex gap-3 mt-5">
                  <AlertDialogCancel
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsDeleteOpen(false);
                    }}
                    className="w-full border border-primary font-semibold"
                  >
                    Cancel
                  </AlertDialogCancel>
                  <AlertDialogAction
                    onClick={handleConfirmDelete}
                    className="w-full bg-destructive text-destructive-foreground hover:bg-destructive/90 font-semibold"
                    disabled={deleteLoading}
                  >
                    {deleteLoading
                      ? isTraining
                        ? "Cancelling..."
                        : "Deleting..."
                      : isTraining
                      ? "Cancel Class"
                      : "Delete"}
                  </AlertDialogAction>
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
        </AlertDialogContent>
      </AlertDialog>
    ) : null;

  // ── Buttons variant ─────────────────────────────────────────────────
  if (variant === "buttons") {
    return (
      <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
        {onView && (
          <Button variant="ghost" size="icon" className="h-6 w-6 p-0" onClick={handleView} title={isTraining ? "View Class" : "View"}>
            <Eye className="h-3 w-3" />
          </Button>
        )}
        {onEdit && canEdit && (
          <Button
            access={access === "write" || access === "full_access"}
            variant="ghost"
            size="icon"
            className="h-6 w-6 p-0"
            onClick={handleEdit}
            title="Edit"
          >
            <Pencil className="h-3 w-3" />
          </Button>
        )}
        {onDelete && canDelete && (
          <Button
            access={access === "full_access" || (isTraining && access === "write")}
            variant="ghost"
            size="icon"
            className="h-6 w-6 p-0 text-red-600 hover:text-red-700"
            onClick={handleDeleteClick}
            title={isTraining ? "Cancel Class" : "Delete"}
          >
            {isTraining ? <XCircle className="h-3 w-3" /> : <Trash2 className="h-3 w-3" />}
          </Button>
        )}
        {renderDeleteDialog()}
      </div>
    );
  }

  // ── Dropdown variant (default) ──────────────────────────────────────
  return access !== "no_access" ? (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
          <Button
            variant="ghost"
            className={`flex p-0 data-[state=open]:bg-muted ${size === "sm" ? "h-6 w-6" : size === "md" ? "h-8 w-8" : "h-10 w-10"
              }`}
          >
            <MoreVertical
              className={
                size === "sm" ? "h-3 w-3" : size === "md" ? "h-4 w-4" : "h-5 w-5"
              }
            />
            <span className="sr-only">Open menu</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="end"
          className="w-40"
          onClick={(e) => e.stopPropagation()}
        >
          {onView && (
            <DropdownMenuItem onClick={handleView}>
              <Eye className="mr-2 h-4 w-4" />
              {isTraining ? "View Details" : "View"}
            </DropdownMenuItem>
          )}
          {onEdit && canEdit && (
            <DropdownMenuItem onClick={handleEdit}>
              <Pencil className="mr-2 h-4 w-4" />
              Edit
            </DropdownMenuItem>
          )}
          {onDelete && canDelete && (
            <DropdownMenuItem
              onClick={handleDeleteClick}
              className="text-red-600 focus:text-red-600 focus:bg-red-50"
            >
              {isTraining ? (
                <>
                  <XCircle className="mr-2 h-4 w-4" />
                  Cancel Class
                </>
              ) : (
                <>
                  <Trash2 className="mr-2 h-4 w-4" />
                  Delete
                </>
              )}
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
      {renderDeleteDialog()}
    </>
  ) : null;
}